import React, { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Link } from "react-router-dom";
import TaskStageBadge from "../components/TaskStageBadge";
import {
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Upload,
  Play,
  Send,
  X,
  Image,
  Video,
  File,
  ChevronDown,
  ChevronUp,
  Calendar,
  Layers,
  Download,
  AlertCircle,
  ShoppingBag,
  UserPlus,
  Link2,
  ExternalLink,
  Paperclip,
} from "lucide-react";

const ArtistTasksPage = () => {
  const { user } = useAuth();
  const { formatAmount } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [poolTasks, setPoolTasks] = useState([]);
  const [contractStatus, setContractStatus] = useState({ accepted: false });
  const [hasRates, setHasRates] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadComment, setUploadComment] = useState("");
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [decliningTask, setDecliningTask] = useState(null);
  const fileInputRef = useRef(null);
  // New state for link submission
  const [versionUrl, setVersionUrl] = useState("");
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [submittingTask, setSubmittingTask] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tasksRes, poolRes, contractRes, profileRes] = await Promise.all([
        api.get("/tasks").catch(() => ({ data: [] })),
        api.get("/artist/task-pool").catch(() => ({ data: [] })),
        api.get("/artist/contract-status").catch(() => ({ data: { accepted: false } })),
        api.get("/profile/me").catch(() => ({ data: { user: {} } })),
      ]);
      
      // Fetch comments for each task
      const tasksWithComments = await Promise.all((tasksRes.data || []).map(async (task) => {
        try {
          const commentsRes = await api.get(`/comments?task_id=${task.id}`);
          return { ...task, comments: commentsRes.data || [] };
        } catch {
          return { ...task, comments: [] };
        }
      }));
      
      setTasks(tasksWithComments);
      setPoolTasks(poolRes.data || []);
      setContractStatus(contractRes.data || { accepted: false });
      
      // Check if artist has set at least one rate (improved check)
      const rates = profileRes.data?.user?.desired_rates || {};
      const serviceRates = profileRes.data?.user?.service_rates || [];
      // hasRates is true if ANY rate is set (desired_rates OR service_rates)
      const hasDesiredRate = Object.values(rates).some(v => v && v > 0);
      const hasServiceRate = serviceRates.length > 0 && serviceRates.some(r => r.rate && r.rate > 0);
      setHasRates(hasDesiredRate || hasServiceRate);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const acceptTask = async (taskId) => {
    if (!contractStatus.accepted) {
      toast.error("You must accept the contract first!");
      return;
    }
    try {
      await api.post(`/artist/tasks/${taskId}/accept`);
      toast.success("Task accepted! You can now start working on it.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept task");
    }
  };

  const takeFromPool = async (taskId) => {
    if (!contractStatus.accepted) {
      toast.error("You must accept the contract first!");
      return;
    }
    try {
      await api.post(`/artist/task-pool/${taskId}/take`);
      toast.success("Task taken! It's now in your active tasks.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to take task");
    }
  };

  const declineTask = async () => {
    if (!decliningTask) return;
    try {
      await api.post(`/artist/tasks/${decliningTask.id}/decline?reason=${encodeURIComponent(declineReason)}`);
      toast.success("Task declined");
      setShowDeclineDialog(false);
      setDecliningTask(null);
      setDeclineReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline task");
    }
  };

  const updateStatus = async (taskId, status) => {
    try {
      await api.put(`/artist/tasks/${taskId}/status?status=${status}`);
      toast.success(`Status updated to ${status.replace("_", " ")}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const uploadVersion = async (taskId, file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("comment", uploadComment);

    try {
      const res = await api.post(`/tasks/${taskId}/version`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Version ${res.data.version.version_number} uploaded!`);
      setUploadComment("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  // New: Submit version via URL link
  const submitVersionLink = async () => {
    if (!versionUrl.trim() || !submittingTask) return;
    
    setUploading(true);
    try {
      const res = await api.post(`/tasks/${submittingTask}/version-link`, {
        url: versionUrl,
        comment: uploadComment || null
      });
      toast.success(`Version ${res.data.version.version_number} submitted for review!`);
      setVersionUrl("");
      setUploadComment("");
      setVersionDialogOpen(false);
      setSubmittingTask(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit version");
    } finally {
      setUploading(false);
    }
  };

  const openVersionDialog = (taskId) => {
    setSubmittingTask(taskId);
    setVersionDialogOpen(true);
  };

  const handleFileSelect = (taskId) => {
    setSelectedTask(taskId);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && selectedTask) {
      uploadVersion(selectedTask, file);
    }
    e.target.value = "";
  };

  const toggleExpand = (taskId) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith("image/")) return Image;
    if (contentType?.startsWith("video/")) return Video;
    return File;
  };

  const canAccept = (task) => ["draft", "new", "assigned"].includes(task.status);
  const canUpload = (task) => ["in_progress", "revision", "review"].includes(task.status);
  const canSubmit = (task) => task.status === "in_progress" && (task.versions?.length || 0) > 0;

  const [showCompleted, setShowCompleted] = useState(false);

  // Group tasks by status
  const pendingTasks = tasks.filter(t => canAccept(t));
  const activeTasks = tasks.filter(t => ["in_progress", "revision"].includes(t.status));
  const reviewTasks = tasks.filter(t => t.status === "review");
  const completedTasks = tasks.filter(t => ["completed"].includes(t.status));
  const approvedTasks = tasks.filter(t => t.status === "approved");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  // Compact Task Row Component
  const TaskRow = ({ task, isPool = false }) => {
    const isExpanded = expandedTasks[task.id];
    const versions = task.versions || [];
    const isOverdue = task.deadline && new Date(task.deadline) < new Date() && !["completed", "approved"].includes(task.status);

    return (
      <div data-testid={`task-row-${task.id}`} className="border-b border-zinc-800/60 last:border-b-0">
        {/* Compact row */}
        <div className={cn(
          "grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center px-4 py-2.5 hover:bg-zinc-800/30 transition-colors",
          isOverdue && "bg-red-950/10"
        )}>
          {/* Name + Status + Project */}
          <div className="flex items-center gap-2.5 min-w-0">
            <TaskStageBadge task={task} size="sm" />
            {isOverdue && (
              <Badge className="bg-red-900/30 text-red-400 border-red-700 text-xs shrink-0">
                <AlertTriangle className="h-3 w-3" />
              </Badge>
            )}
            {isPool && (
              <Badge className="bg-purple-900/30 text-purple-400 border-purple-700 text-xs shrink-0">
                <ShoppingBag className="h-3 w-3" />
              </Badge>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-zinc-50 truncate">{task.name}</p>
              <p className="text-xs text-zinc-500 truncate">{task.project_name}</p>
            </div>
          </div>

          {/* Deadline */}
          <div className="text-right shrink-0">
            {task.deadline && (
              <p className={cn("text-xs flex items-center gap-1 whitespace-nowrap", isOverdue ? "text-red-400" : "text-zinc-500")}>
                <Calendar className="h-3 w-3" />
                {task.deadline}
              </p>
            )}
          </div>

          {/* Amount */}
          <div className="text-right shrink-0">
            <p className="text-sm font-mono text-green-400 whitespace-nowrap">
              {formatAmount(task.agreed_price, task.currency || "UAH")}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            {isPool && (
              <Button
                size="sm"
                onClick={() => takeFromPool(task.id)}
                disabled={!contractStatus.accepted}
                className="bg-purple-600 hover:bg-purple-700 text-white h-7 text-xs px-2"
                data-testid={`take-task-${task.id}`}
              >
                Take
              </Button>
            )}
            {!isPool && canAccept(task) && (
              <>
                <Button
                  size="sm"
                  onClick={() => acceptTask(task.id)}
                  disabled={!contractStatus.accepted}
                  className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs px-2"
                  data-testid={`accept-task-${task.id}`}
                >
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setDecliningTask(task); setShowDeclineDialog(true); }}
                  className="text-red-400 hover:bg-red-900/30 h-7 text-xs px-2"
                  data-testid={`decline-task-${task.id}`}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
            {canUpload(task) && (
              <Button
                size="sm"
                onClick={() => openVersionDialog(task.id)}
                disabled={uploading}
                className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs px-2"
                data-testid={`submit-work-${task.id}`}
              >
                <Send className="h-3 w-3 mr-1" /> Submit
              </Button>
            )}
            {canSubmit(task) && (
              <Button
                size="sm"
                onClick={() => updateStatus(task.id, "review")}
                className="bg-cyan-600 hover:bg-cyan-700 text-white h-7 text-xs px-2"
              >
                <Send className="h-3 w-3 mr-1" /> Review
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => toggleExpand(task.id)}
              className="text-zinc-500 hover:text-zinc-300 h-7 w-7 p-0"
              data-testid={`toggle-details-${task.id}`}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="px-4 pb-4 pt-1 bg-zinc-900/40 space-y-3 border-t border-zinc-800/40">
            {task.description && (
              <div>
                <h4 className="text-xs font-medium text-zinc-500 uppercase mb-1">Description</h4>
                <p className="text-sm text-zinc-300">{task.description}</p>
              </div>
            )}

            {task.brief && (
              <div>
                <h4 className="text-xs font-medium text-zinc-500 uppercase mb-1">Brief</h4>
                <p className="text-sm text-zinc-300 whitespace-pre-wrap">{task.brief}</p>
              </div>
            )}

            {task.reference_files?.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-zinc-500 uppercase mb-2">References</h4>
                <div className="flex flex-wrap gap-2">
                  {task.reference_files.map((ref, i) => {
                    const isLink = ref.type === "link" || ref.url;
                    const RefIcon = isLink ? ExternalLink : getFileIcon(ref.content_type);
                    return (
                      <a
                        key={i}
                        href={isLink ? ref.url : `${process.env.REACT_APP_BACKEND_URL}/api/files/${ref.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-2.5 py-1.5 bg-zinc-800 rounded-md hover:bg-zinc-700 transition-colors"
                      >
                        <RefIcon className="h-3.5 w-3.5 text-zinc-400" />
                        <span className="text-xs text-zinc-300 max-w-[120px] truncate">
                          {isLink ? (ref.title || "Link") : ref.filename}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {versions.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-zinc-500 uppercase mb-2">
                  Submissions ({versions.length})
                </h4>
                <div className="space-y-1.5">
                  {versions.map((v, i) => {
                    const isLink = v.type === "link" || v.url;
                    const VIcon = isLink ? Link2 : getFileIcon(v.content_type);
                    const statusBadge = v.internal_status === "pending_review"
                      ? { text: "Pending", color: "bg-yellow-900/30 text-yellow-400 border-yellow-700" }
                      : v.internal_status === "approved"
                      ? { text: "Sent", color: "bg-blue-900/30 text-blue-400 border-blue-700" }
                      : v.internal_status === "rejected"
                      ? { text: "Revision", color: "bg-red-900/30 text-red-400 border-red-700" }
                      : v.approved
                      ? { text: "OK", color: "bg-green-900/30 text-green-400 border-green-700" }
                      : null;

                    return (
                      <div key={i} className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-md text-xs",
                        v.internal_status === "rejected" ? "bg-red-900/15 border border-red-800/20" : "bg-zinc-800/40"
                      )}>
                        <span className="font-mono text-zinc-400 shrink-0">v{v.version_number}</span>
                        <VIcon className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          {isLink ? (
                            <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300">
                              View Work
                            </a>
                          ) : (
                            <span className="text-zinc-300 truncate block">{v.filename}</span>
                          )}
                          {v.internal_status === "rejected" && v.internal_review_comment && (
                            <p className="text-red-400 mt-0.5">Feedback: {v.internal_review_comment}</p>
                          )}
                        </div>
                        <span className="text-zinc-600 whitespace-nowrap shrink-0">
                          {new Date(v.uploaded_at).toLocaleDateString()}
                        </span>
                        {statusBadge && (
                          <Badge className={cn("text-xs py-0 px-1.5", statusBadge.color)}>{statusBadge.text}</Badge>
                        )}
                        {!isLink && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${v.id}`} target="_blank" rel="noopener noreferrer" className="hover:text-zinc-300 text-zinc-500">
                            <Download className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Client Versions Counter */}
            {(() => {
              const clientVersions = versions.filter(v => v.internal_status === "approved").length;
              const freeLimit = task.free_revisions_limit || 3;
              if (clientVersions === 0) return null;
              return (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500">Sent to client:</span>
                  <Badge variant="outline" className={cn("text-xs", clientVersions < freeLimit ? "bg-green-500/10 text-green-400 border-green-500/30" : "bg-orange-500/10 text-orange-400 border-orange-500/30")}>
                    {clientVersions}/{freeLimit} free
                  </Badge>
                  {(task.paid_revisions || 0) > 0 && (
                    <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30">+{task.paid_revisions} paid</Badge>
                  )}
                </div>
              );
            })()}

            {/* Comments */}
            {task.comments?.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-zinc-500 uppercase mb-2">Comments</h4>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {task.comments.slice().reverse().map((comment, i) => (
                    <div key={i} className={cn("p-2.5 rounded-md text-xs", comment.is_revision_request ? "bg-red-900/15 border border-red-800/20" : comment.user_role === "client" ? "bg-blue-900/15 border border-blue-800/20" : "bg-zinc-800/40")}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="font-medium text-zinc-300">{comment.user_name}</span>
                        <span className="text-zinc-600">{comment.user_role === "client" ? "Client" : "Team"}</span>
                        {comment.is_revision_request && <span className="text-red-400">Rev #{comment.revision_number}</span>}
                        <span className="text-zinc-700 ml-auto">{new Date(comment.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-zinc-300 whitespace-pre-wrap">{comment.content}</p>
                      {comment.attachment && (
                        <a href={comment.attachment} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 mt-1">
                          <Paperclip className="h-3 w-3" /> View attachment
                        </a>
                      )}
                      {comment.feedback_link && (
                        <a href={comment.feedback_link} target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 flex items-center gap-1 mt-1">
                          <ExternalLink className="h-3 w-3" /> Feedback
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canUpload(task) && (
              <div className="pt-2 border-t border-zinc-700/50">
                <Button size="sm" onClick={() => openVersionDialog(task.id)} className="bg-blue-600 hover:bg-blue-700 text-white h-7 text-xs">
                  <Send className="h-3 w-3 mr-1" /> Submit Work
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Compact Table Section
  const TaskSection = ({ title, tasks, icon: Icon, color, isPool = false, collapsible = false, defaultHidden = false }) => {
    const [visible, setVisible] = useState(!defaultHidden);
    if (tasks.length === 0) return null;
    return (
      <div data-testid={`task-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        {collapsible ? (
          <button
            onClick={() => setVisible(v => !v)}
            className={cn("text-xs font-medium uppercase flex items-center gap-2 mb-2 w-full hover:opacity-80 transition-opacity", color)}
            data-testid={`toggle-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {title} ({tasks.length})
            {visible ? <ChevronUp className="h-3.5 w-3.5 ml-1" /> : <ChevronDown className="h-3.5 w-3.5 ml-1" />}
          </button>
        ) : (
          <h2 className={cn("text-xs font-medium uppercase flex items-center gap-2 mb-2", color)}>
            <Icon className="h-3.5 w-3.5" />
            {title} ({tasks.length})
          </h2>
        )}
        {visible && (
          <div className="border border-zinc-800 rounded-lg overflow-hidden bg-zinc-900/30">
            {tasks.map(task => <TaskRow key={task.id} task={task} isPool={isPool} />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="artist-tasks-page" className="space-y-6">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,video/*,.exr,.max,.fbx,.obj"
        onChange={handleFileChange}
      />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
          <Layers className="h-7 w-7 text-orange-500" />
          My Tasks
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {tasks.length} assigned • {poolTasks.length} available in pool
        </p>
      </div>

      {/* Contract Warning */}
      {!contractStatus.accepted && (
        <Card className="bg-amber-900/20 border-amber-700">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-medium text-amber-400">Contract Required</h3>
                <p className="text-sm text-zinc-300 mt-1">
                  You must accept the collaboration contract before you can take or accept tasks.
                </p>
                <Link to="/artist-contract">
                  <Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700 text-white">
                    <FileText className="h-4 w-4 mr-2" />
                    View & Accept Contract
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rates Required Warning */}
      {!hasRates && (
        <Card className="bg-amber-900/20 border-amber-700">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-medium text-amber-400">Rates Required</h3>
                <p className="text-sm text-zinc-300 mt-1">
                  Please set your rates so project managers can see your expectations when assigning tasks to you.
                </p>
                <Link to="/profile">
                  <Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700 text-white">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Set My Rates
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Task Pool */}
      {poolTasks.length > 0 && (
        <TaskSection 
          title="Available Tasks (Pool)" 
          tasks={poolTasks} 
          icon={ShoppingBag} 
          color="text-purple-400"
          isPool={true}
        />
      )}

      {/* My Tasks */}
      <TaskSection title="Pending Acceptance" tasks={pendingTasks} icon={Clock} color="text-blue-400" />
      <TaskSection title="Active Work" tasks={activeTasks} icon={Play} color="text-amber-400" />
      <TaskSection title="Under Review" tasks={reviewTasks} icon={Send} color="text-cyan-400" />
      <TaskSection title="Approved" tasks={approvedTasks} icon={CheckCircle2} color="text-green-400" collapsible defaultHidden />
      <TaskSection title="Completed" tasks={completedTasks} icon={CheckCircle2} color="text-green-500" collapsible defaultHidden />

      {/* No tasks at all */}
      {tasks.length === 0 && poolTasks.length === 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-zinc-600 mb-4" />
            <h3 className="text-lg font-medium text-zinc-50 mb-2">No Tasks Available</h3>
            <p className="text-zinc-500">Tasks assigned to you or available in the pool will appear here</p>
          </CardContent>
        </Card>
      )}

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Decline Task</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-400 mb-4">
              Are you sure you want to decline &quot;{decliningTask?.name}&quot;?
            </p>
            <Textarea
              placeholder="Reason for declining (optional)..."
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-zinc-50"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeclineDialog(false)}
              className="border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={declineTask}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Decline Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Work Dialog (Link-based) */}
      <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Submit Work</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Work URL *</label>
              <Input
                value={versionUrl}
                onChange={(e) => setVersionUrl(e.target.value)}
                placeholder="https://drive.google.com/... or Dropbox link"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
              <p className="text-xs text-zinc-500">Paste link to your work (Google Drive, Dropbox, etc.)</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-zinc-400">Comment (Optional)</label>
              <Textarea
                value={uploadComment}
                onChange={(e) => setUploadComment(e.target.value)}
                placeholder="Describe what's included in this version..."
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                rows={3}
              />
            </div>
            <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
              <p className="text-xs text-zinc-400">
                <AlertCircle className="h-3 w-3 inline mr-1" />
                Your work will be reviewed internally before being sent to the client
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setVersionDialogOpen(false);
                setVersionUrl("");
                setUploadComment("");
              }}
              className="border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={submitVersionLink}
              disabled={!versionUrl.trim() || uploading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {uploading ? "Submitting..." : "Submit for Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtistTasksPage;
